import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLatestRef } from '../hooks/useLatestRef';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-2xl',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useLatestRef(onClose);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] overflow-y-auto overscroll-y-contain bg-black/55 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Dialog'}
    >
      <div
        className="flex min-h-full w-full flex-col items-stretch justify-start px-4 pt-6 pb-10 sm:min-h-[100dvh] sm:items-center sm:justify-center sm:px-6 sm:py-10"
        onClick={onClose}
      >
        <div
          ref={dialogRef}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          className={`relative mx-auto my-0 w-full ${maxWidth} flex max-h-[min(90dvh,calc(100dvh-2.5rem))] min-h-0 flex-col overflow-hidden rounded-lg bg-white shadow-2xl animate-scale-up focus:outline-none sm:my-auto sm:max-h-[min(88vh,calc(100dvh-3rem))]`}
        >
          {title && (
            <div className="flex shrink-0 justify-between items-center gap-4 px-5 py-4 sm:px-8 sm:py-5 border-b border-stone-100">
              <h2 className="text-lg sm:text-2xl font-bold serif text-stone-900 text-left">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 p-2 text-stone-400 hover:text-stone-900 transition-colors rounded-lg hover:bg-stone-50"
                aria-label="Close dialog"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] scroll-py-6 px-5 py-5 sm:px-8 sm:py-6">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
